/**
 * Copyright (C) 2021 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import { Box, IconButton, Snackbar } from '@mui/material';
import ErrorMessage from './ErrorMessage';
import Close from '@mui/icons-material/Close';
 
interface ErrorBoundaryProps {
    message?: string;
    onReset?: Function;
}

interface ErrorBoundaryState {
    error: any; 
    errorInfo: null | React.ErrorInfo;
    snackbarOpen: boolean;
}

/**
 * React error boundary that will show a snackbar when a child component cannot be rendered due to a runtime error. 
 * See https://reactjs.org/docs/error-boundaries.html for more details.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: {}) {
        super(props);
        this.state = { error: null, errorInfo: null, snackbarOpen: false };
    }

    componentDidCatch(error: any, errorInfo: React.ErrorInfo) {
        console.error(error);
        console.error(JSON.stringify(errorInfo));
        console.error(errorInfo.componentStack);

        this.setState({
            error,
            errorInfo,
            snackbarOpen: true
        })
    }

    private onCloseSnackbar = () => {
        if (this.props.onReset) {
            this.props.onReset();

            // remove error if this component resets the state
            this.setState({ 
                error: null, 
                errorInfo: null, 
                snackbarOpen: false 
            });
        } else {
            // do not remove the error if this component does not reset the state
            this.setState({
                snackbarOpen: false 
            })
        }
    }

    render() {
        if (this.state.errorInfo) {
            return (
              <Snackbar open={this.state.snackbarOpen} autoHideDuration={6000}>
                <Box display="flex" bgcolor="#FC6964">
                  <ErrorMessage>{this.props.message ? this.props.message : 'Something went wrong...'}</ErrorMessage>
                  <IconButton color="inherit" key="close" aria-label="Close" onClick={this.onCloseSnackbar}>
                    <Close />
                  </IconButton>
                </Box>
              </Snackbar>
            );
        }
        
        return this.props.children;
    }
    
 }

 export default ErrorBoundary;